export type GameRarity = "common" | "uncommon" | "rare" | "epic" | "legendary";

export interface RarityDefinition {
  id: GameRarity;
  label: string;
  probabilityBps: number;
  color: string;
  next: GameRarity | null;
}

export interface GameInventoryItem {
  id: string;
  label: string;
  rarity: GameRarity;
  quantity: number;
  emoji: string;
}

export interface GameState {
  serverTime: string;
  wallet: { earned: number; spent: number; balance: number };
  draw: { cost: number; nextEligibleAt: string | null; eligible: boolean };
  inventory: GameInventoryItem[];
  demo?: boolean;
}

export interface GameReward {
  transactionId: string;
  item: GameInventoryItem;
  isNew: boolean;
  balance: number;
  nextEligibleAt: string | null;
}

export const DRAW_COST = 100_000;
export const DRAW_COOLDOWN_MS = 30 * 60 * 1_000;
export const MAX_VISIBLE_TOKENS = 9_999_999_999;

export const RARITIES: readonly RarityDefinition[] = [
  { id: "common", label: "普通", probabilityBps: 8_000, color: "#f8fafc", next: "uncommon" },
  { id: "uncommon", label: "优良", probabilityBps: 1_600, color: "#63dc73", next: "rare" },
  { id: "rare", label: "稀有", probabilityBps: 320, color: "#5aa8ff", next: "epic" },
  { id: "epic", label: "史诗", probabilityBps: 72, color: "#c77dff", next: "legendary" },
  { id: "legendary", label: "传说", probabilityBps: 8, color: "#ffc857", next: null },
] as const;

const ITEM_PREFIX: Record<GameRarity, string> = {
  common: "C",
  uncommon: "U",
  rare: "R",
  epic: "E",
  legendary: "L",
};

const ITEM_EMOJI: Record<GameRarity, readonly string[]> = {
  common: ["🧶", "🪶", "🧦", "🥣", "📎", "🪵", "🪨", "🍞", "🧩", "🧸"],
  uncommon: ["🐟", "🌿", "🍀", "🎀", "🪴", "🛹", "🍄", "🎈", "🧁", "🛶"],
  rare: ["🎩", "💎", "🧿", "🪄", "🎭", "🛸", "🎺", "🦋", "🧊", "⚓"],
  epic: ["🔮", "🌌", "🐉", "⚡", "🎇", "🪐", "🦄", "🏰", "🗡️", "🧙"],
  legendary: ["👑", "☀️", "🏆", "🌟", "🔥", "🪽", "🐲", "💫", "🛡️", "⚜️"],
};

export function itemCatalog(): GameInventoryItem[] {
  return RARITIES.flatMap((rarity) => ITEM_EMOJI[rarity.id].map((emoji, index) => {
    const number = String(index + 1).padStart(2, "0");
    return { id: `${ITEM_PREFIX[rarity.id]}${number}`, label: `${rarity.label}${number}`, rarity: rarity.id, quantity: 0, emoji };
  }));
}

export function rarityDefinition(rarity: GameRarity): RarityDefinition {
  return RARITIES.find((candidate) => candidate.id === rarity)!;
}

export function rollRarity(randomBps: number): GameRarity {
  if (!Number.isInteger(randomBps) || randomBps < 0 || randomBps >= 10_000) throw new RangeError("randomBps must be an integer from 0 to 9999");
  let cursor = 0;
  for (const rarity of RARITIES) {
    cursor += rarity.probabilityBps;
    if (randomBps < cursor) return rarity.id;
  }
  throw new Error("rarity weights must total 10,000");
}

export function secureRandomBps(): number {
  const values = new Uint32Array(1);
  const range = 0x1_0000_0000;
  const limit = range - (range % 10_000);
  do crypto.getRandomValues(values); while (values[0] >= limit);
  return values[0] % 10_000;
}

export function formatTokenCount(value: number): string {
  const normalized = Math.max(0, Math.floor(value));
  if (normalized > MAX_VISIBLE_TOKENS) return `${MAX_VISIBLE_TOKENS.toLocaleString("en-US")}+`;
  return normalized.toLocaleString("en-US");
}

export function effectiveTokens(input: number, output: number, cacheWrite: number): number {
  return Math.max(0, input) + Math.max(0, output) + Math.max(0, cacheWrite);
}

export function availableDuplicateCount(item: GameInventoryItem): number {
  return Math.max(0, item.quantity - 1);
}

export function validateExchangeSelection(items: GameInventoryItem[]): { ok: true; source: GameRarity; target: GameRarity } | { ok: false; error: string } {
  if (items.length !== 10) return { ok: false, error: "请选择正好10件重复物品" };
  const source = items[0]?.rarity;
  if (!source || items.some((item) => item.rarity !== source)) return { ok: false, error: "10件物品必须为相同品质" };
  const target = rarityDefinition(source).next;
  if (!target) return { ok: false, error: "传说品质不能进行淘汰合同" };
  const requested = new Map<string, number>();
  for (const item of items) requested.set(item.id, (requested.get(item.id) ?? 0) + 1);
  for (const [id, quantity] of requested) {
    const item = items.find((candidate) => candidate.id === id)!;
    if (quantity > availableDuplicateCount(item)) return { ok: false, error: `${item.label}的重复数量不足` };
  }
  return { ok: true, source, target };
}
